import { Box, Container, SystemStyleObject, VStack } from "@chakra-ui/react"
import { useDragAwareClick } from "@pathwright/utils/src/event/click"
import { AUGMENTATION_SECTION_FOCUS_TARGET_CSS } from "@pathwright/ux/src/components/augmentation/sectionFocus"
import { LayoutGroup, motion } from "framer-motion"
import SchoolErrorBoundary from "../error/SchoolErrorBoundary"
import CoverPageAugmentation from "./CoverPageAugmentation"
import CoverPageDescription from "./CoverPageDescription"
import CoverPageHeader from "./CoverPageHeader"
import CoverPageLibraryNavigation from "./CoverPageLibraryNavigation"
import CoverPageLoadingOverlay from "./CoverPageLoadingOverlay"
import CoverPageMore from "./CoverPageMore"
import CoverPageOffers from "./CoverPageOffers"
import CoverPageProvider, { useCoverPageContext } from "./CoverPageProvider"
import { ANIMATION_DURATION, EASE_BEZIER } from "./animations"
import { COVER_PAGE_PADDING } from "./constants"
import CoverPageContributors from "./contributors/CoverPageContributors"
import CoverPageImmersiveContainer from "./immersive/CoverPageImmersive"
import CoverPagePathPreview from "./path-preview/CoverPagePathPreview"
import { CoverPageContainerProps } from "./types"

export const CoverPageWrapper = ({
  children,
  className,
  css,
  isFullscreen
}: {
  children: React.ReactNode
  className?: string
  css?: SystemStyleObject
  isFullscreen?: boolean
}) => {
  return (
    <VStack
      className={`CoverPageWrapper chakra-ui-reset-css ${className || ""}`}
      gap={0}
      h="100%"
      p={0}
      w="100%"
      maxW={isFullscreen ? "100%" : "1000px"}
      borderRadius={isFullscreen ? "0" : { base: "0", md: "card" }}
      justifySelf="center"
      alignSelf="center"
      maxH={{ base: "100vh", md: "100%" }}
      transition="max-height 0.2s ease-out"
      position="absolute"
      inset={0}
      zIndex={1}
      css={{
        ...AUGMENTATION_SECTION_FOCUS_TARGET_CSS,
        "& p, & div, & span": {
          lineHeight: "1.4"
        },
        //  Hide horizontal scrollbars in carousel components
        "& .carousel-scroller": {
          "&::-webkit-scrollbar": {
            display: "none"
          },
          msOverflowStyle: "none", // IE and Edge
          scrollbarWidth: "none" // Firefox
        },
        // Overlay scrollbar styling
        "&::-webkit-scrollbar": {
          width: "8px"
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent"
        },
        "&::-webkit-scrollbar-thumb": {
          background: "rgba(0, 0, 0, 0.2)",
          borderRadius: "4px",
          "&:hover": {
            background: "rgba(0, 0, 0, 0.3)"
          }
        },
        // Firefox overlay scrollbar
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
        ...css
      }}
    >
      {children}
    </VStack>
  )
}

const CoverPage = () => {
  const scrollContainerRef = useCoverPageContext((v) => v.scrollContainerRef)

  return (
    <Box w="100%" h="100%" pos="relative">
      <CoverPageWrapper>
        <VStack
          ref={scrollContainerRef}
          w="100%"
          h="100%"
          gap={0}
          borderRadius={{
            base: 0,
            md: "card"
          }}
          overflowY="auto"
        >
          <CoverPageHeader />
          <Container
            maxW="1000px"
            p={0}
            // On mobile add a bit more bottom padding to allow for the toolbar
            pb={{
              base: `calc(${COVER_PAGE_PADDING.base} + 20px)`,
              md: COVER_PAGE_PADDING.md
            }}
            bgColor="backgroundSecondary"
          >
            <CoverPageDescription />
            <CoverPagePathPreview />
            <CoverPageOffers />
            <CoverPageContributors />
            <CoverPageMore />
          </Container>
        </VStack>
      </CoverPageWrapper>
    </Box>
  )
}

const CoverPageBackground = () => {
  return (
    <Box
      className="CoverPageBackground"
      pos="absolute"
      inset={0}
      bgColor="backgroundSecondary"
      overflow="hidden"
      borderRadius={{ base: "0", md: "card" }}
    />
  )
}

const CoverPageSurface = () => {
  return (
    <Box
      className="CoverPageSurface"
      w="100%"
      maxW="1000px"
      h="100%"
      maxH={{ base: "100%", md: "calc(100vh - 80px)" }}
      pos="relative"
      m="auto"
      top="50%"
      transform="translateY(-50%)"
    >
      <CoverPageBackground />
      <CoverPage />
      <CoverPageLoadingOverlay />
    </Box>
  )
}

const CoverPageContainer = (props: CoverPageContainerProps) => {
  const clickHandlers = useDragAwareClick({
    onClick: (event) => {
      const targetId = (event.target as HTMLElement).id
      // Only close on direct container clicks that weren't preceded by a drag
      if (targetId === "cover-page-container") {
        props.onClose?.()
      }
    }
  })

  return (
    <SchoolErrorBoundary>
      <CoverPageProvider {...props}>
        <LayoutGroup id="cover-page">
          <motion.div
            id="cover-page-container"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
            transition={{
              duration: ANIMATION_DURATION.fade,
              ease: EASE_BEZIER
            }}
            style={{ height: "100%" }}
            {...clickHandlers}
          >
            <CoverPageAugmentation Primary={CoverPageSurface} />
          </motion.div>
          <CoverPageImmersiveContainer />
          {/* Cannot scope library navigation to licensor school for now */}
          {!props.licensorSchoolId && <CoverPageLibraryNavigation />}
        </LayoutGroup>
      </CoverPageProvider>
    </SchoolErrorBoundary>
  )
}

export default CoverPageContainer
